import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CleaningAreaRowItemProps } from '../../../../../../../../types/CleaningAreaList.types';
import { animateCleaningAreaListLayoutChanges } from '../../../../../../../../utils/cleaningAreaList';
import { CLEANING_AREA_LIST_SORTABLE_TRANSITION } from '../../../../../../../../utils/constants';
import { CleaningAreaRowContentBlock } from '../CleaningAreaRowContentBlock/CleaningAreaRowContentBlock';
import { CleaningAreaRow } from './CleaningAreaRowItem.styles';

export const CleaningAreaRowItem = ({
  area,
  position,
  onToggleArea,
  disabled,
  isAnyDragging,
  measureRef,
}: CleaningAreaRowItemProps): JSX.Element => {
  const isSelected = position !== null;
  const isDraggable = isSelected && !disabled;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: area.id,
    disabled: !isDraggable,
    animateLayoutChanges: animateCleaningAreaListLayoutChanges,
    transition: CLEANING_AREA_LIST_SORTABLE_TRANSITION,
  });

  const setRefs = (node: HTMLDivElement | null): void => {
    setNodeRef(node);
    const ref = measureRef;
    if (ref) {
      ref.current = node;
    }
  };

  // While a drag is in progress, sibling rows must stay put — only the drop indicator line
  // and the active row's grey overlay show the target position. Outside of an active drag
  // (e.g. a checkbox toggle reordering the list), the transform still animates the FLIP move.
  const rowStyle = isAnyDragging
    ? { cursor: disabled ? 'default' : 'pointer' }
    : { transform: CSS.Transform.toString(transform), transition, cursor: disabled ? 'default' : 'pointer' };

  return (
    <CleaningAreaRow
      ref={setRefs}
      style={rowStyle}
      align="center"
      onClick={disabled ? undefined : (): void => onToggleArea(area.id)}
    >
      <CleaningAreaRowContentBlock
        area={area}
        position={position}
        isDraggable={isDraggable}
        onToggleArea={onToggleArea}
        disabled={disabled}
        dragHandleProps={isDraggable ? { attributes, listeners } : undefined}
        isDraggingSource={isDragging}
      />
    </CleaningAreaRow>
  );
};
